import { type MouseEvent, type ReactNode, useCallback, useState } from 'react';
import { Modal, type ModalProps } from '@mantine/core';
import { UnsavedChangesModal } from '@/components/modals/UnsavedChangesModal/UnsavedChangesModal';
import { UNSAVED_CHANGES_MODAL_VARIANT } from '@/types';
import { GuardedModalCloseProvider } from './GuardedModalCloseContext';

type GuardedModalProps = {
  hasUnsavedChanges?: boolean;
  onDiscard?: () => void;
  unsavedChangesSecondaryActions?: ReactNode[];
  children: ReactNode | ((requestClose: () => void) => ReactNode);
} & ModalProps;

export const GuardedModal = ({
  hasUnsavedChanges = false,
  onDiscard,
  unsavedChangesSecondaryActions,
  onClose,
  opened,
  children,
  overlayProps,
  closeOnClickOutside: _closeOnClickOutside,
  ...modalProps
}: GuardedModalProps) => {
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);

  const handleRequestClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setIsUnsavedChangesModalOpen(true);
      return;
    }

    onClose();
  }, [hasUnsavedChanges, onClose]);

  const handleLeaveWithoutSaving = useCallback(() => {
    setIsUnsavedChangesModalOpen(false);
    onDiscard?.();
    onClose();
  }, [onClose, onDiscard]);

  const handleUnsavedChangesModalClose = useCallback(() => {
    setIsUnsavedChangesModalOpen(false);
  }, []);

  const handleOverlayMouseDown = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      overlayProps?.onMouseDown?.(event);

      if (event.target !== event.currentTarget) {
        return;
      }

      handleRequestClose();
    },
    [handleRequestClose, overlayProps]
  );

  const renderedChildren = typeof children === 'function' ? children(handleRequestClose) : children;

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleRequestClose}
        closeOnClickOutside={false}
        overlayProps={{
          ...overlayProps,
          onMouseDown: handleOverlayMouseDown,
          mod: { testid: 'guarded-modal-overlay' },
        }}
        {...modalProps}
      >
        <GuardedModalCloseProvider value={handleRequestClose}>{renderedChildren}</GuardedModalCloseProvider>
      </Modal>

      <UnsavedChangesModal
        opened={isUnsavedChangesModalOpen}
        onClose={handleUnsavedChangesModalClose}
        onLeave={handleLeaveWithoutSaving}
        variant={UNSAVED_CHANGES_MODAL_VARIANT.OVERLAY_GUARD}
        secondaryActions={unsavedChangesSecondaryActions}
      />
    </>
  );
};
